import { getUrlPrefix } from '~/lang/locales'
import { datepickerAvailabilityResultsSchema } from './schemas'
import { DatepickerAvailabilityResultsDay } from './types'
import {
  DatepickerFetchError,
  DatepickerValidationError,
} from './DatepickerError'

// Default timeout for datepicker requests (30 seconds)
const DATEPICKER_TIMEOUT = 30000

export class DatepickerAvailabilityServiceFetch {
  async get(
    langCode: string,
    slug: string,
    categories: Array<string> = [],
    pitchtypeId?: string,
  ): Promise<DatepickerAvailabilityResultsDay[]> {
    const params = categories.reduce((categoryParams, id) => {
      categoryParams.append('type', String(id))
      return categoryParams
    }, new URLSearchParams())
    if (pitchtypeId) {
      params.append('pitchtype_id', String(pitchtypeId))
    }
    const urlPrefix = getUrlPrefix(langCode)
    const url = `/_/fallback${urlPrefix}/_/datepicker/${slug}${params.size ? '?' + params.toString() : ''}`

    let response: any

    // Step 1: Fetch the data
    try {
      response = await $fetch(url, {
        timeout: DATEPICKER_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty array for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return []
      }

      // Handle 401/403 as bot protection - return empty availability gracefully
      // These responses are typically Cloudflare blocking bots/crawlers
      // 401: Cloudflare blocking unauthenticated bots
      // 403: Cloudflare blocking suspected malicious traffic
      // Returning empty array prevents XState machine from entering error state
      const httpStatus =
        error?.statusCode || error?.status || error?.response?.status
      if (httpStatus === 401 || httpStatus === 403) {
        // Log to console in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[DatepickerAvailability] ${httpStatus} ${httpStatus === 401 ? 'Unauthorized' : 'Forbidden'} - likely bot protection, returning empty availability`,
            { url, slug },
          )
        }
        return [] // Empty availability - no dates available
      }

      const fetchError = new DatepickerFetchError(
        url,
        slug,
        categories,
        pitchtypeId,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw fetchError
    }

    // Step 2: Validate with Zod schema
    try {
      return datepickerAvailabilityResultsSchema.parse(
        response,
      ) as DatepickerAvailabilityResultsDay[]
    } catch (error: any) {
      const validationError = new DatepickerValidationError(
        url,
        slug,
        categories,
        pitchtypeId,
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }
}
